import dayjs from 'dayjs';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Checkbox from 'components/global/Checkbox';
import ConfirmationDialog from 'components/global/ConfirmationDialog';
import Popper from 'components/global/Popper';
import Radio from 'components/global/Radio';
import Tag from 'components/global/Tag';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import DynamicListDateInput from 'components/list/FilterInputs/DynamicListDateInput';
import DynamicListMultiSelect from 'components/list/FilterInputs/DynamicListMultiSelect';
import DynamicListNumberInput from 'components/list/FilterInputs/DynamicListNumberInput';
import DynamicListStringInput from 'components/list/FilterInputs/DynamicListStringInput';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { setListMapView } from 'stores/layoutSlice';

import { Breakpoints } from 'utils/const.styles';

import {
	faCheck,
	faFilter,
	faList,
	faLocationDot,
	faTimes,
	faTrash,
	faTrashAlt
} from '@fortawesome/pro-light-svg-icons';

import ColumnsDndItem from './ColumnsDndItem';
import ActionsMenu from './DynamicListActionsMenu';
import DynamicListExportButton from './DynamicListExportButton';
import * as S from './DynamicListFilter.styled';
import { DefaultFilterActionMap, FilterAction, FilterConfig, FilterInput, FilterType } from './DynamicListFilterUtils';
import DynamicListChips from './FilterInputs/DynamicListChips';
import DynamicListEnumSelect from './FilterInputs/DynamicListEnumSelect';
import DynamicListFloatInput from './FilterInputs/DynamicListFloatInput';

/**
 * @author Burak Unsal
 */
const DynamicListFilter = (props) => {
	const {
		fetchUrl,
		actionsMenu,
		columns,
		originalColumns,
		visibleColumns,
		selectionModel,
		setColumns,
		setHiddenColumns,
		setTableHiddenColumns,
		filterModel,
		onFilterModelChange,
		onSelectionModelChange,
		toggleAllRowsSelected,
		showHeader,
		showFilters,
		showMapViewButton,
		multiSelectFilters,
		exportName,
		exportNameTranslationObject,
		sortModel,
		requestParameters
	} = props;

	const { t } = useTranslation();
	const { showSuccess, showWarning } = useAlert();

	const [selectedFilterColumn, setSelectedFilterColumn] = useState();
	const [selectedFilterAction, setSelectedFilterAction] = useState();
	const [selectedFilterValue, setSelectedFilterValue] = useState('');
	const selectedFilterInputType = useRef(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const isFiltered = useMemo(
		() => filterModel.some((filter) => filter.column.filterVisible !== false),
		[filterModel]
	);
	const isSingleVisibleColumn = useMemo(
		() => visibleColumns?.filter((col) => col.id !== 'rowSelection' && col.id !== 'rowActions').length === 1,
		[visibleColumns]
	);

	const handleColumnVisibilityChange = useCallback(
		(column) => {
			const columnsTemplate = structuredClone(columns);
			columnsTemplate.find((col) => col.accessor === column.accessor).show = column.show === false;
			setColumns(columnsTemplate);
			const hiddenColumnsTemplate = columnsTemplate
				.filter((column) => column.show === false)
				.map((column) => column.accessor);
			setHiddenColumns(hiddenColumnsTemplate);
			setTableHiddenColumns(hiddenColumnsTemplate);
		},
		[columns]
	);

	const resetColumns = useCallback(() => {
		const columnsTemplate = structuredClone(originalColumns);
		setColumns(columnsTemplate);
		const hiddenColumnsTemplate = columnsTemplate
			.filter((column) => column.show === false)
			.map((column) => column.accessor);
		setHiddenColumns(hiddenColumnsTemplate);
		setTableHiddenColumns(hiddenColumnsTemplate);
		showSuccess({
			message: t('successMessages.columnsReset')
		});
	}, []);

	const addFilterObject = (filterObject) => {
		let filtersTemplate = [...filterModel];
		let isThere = false;
		filtersTemplate = filtersTemplate.map((filter) => {
			if (filter.column.accessor === filterObject.column.accessor) {
				isThere = true;
				return filterObject;
			}

			return filter;
		});
		!isThere && filtersTemplate.push(filterObject);
		onFilterModelChange(filtersTemplate);
	};

	const clearFilter = useCallback(() => {
		setSelectedFilterColumn();
		setSelectedFilterAction();
		setSelectedFilterValue('');
		setFilterColumnsMenuSearchValue(null);
	}, []);

	const handleCancelFilterButtonClick = useCallback(() => {
		clearFilter();
	}, []);

	const handleApplyFilterButtonClick = (closePopper) => {
		if (
			((selectedFilterValue === '' || selectedFilterValue === null) &&
				selectedFilterAction !== FilterAction.isEmpty &&
				selectedFilterAction !== FilterAction.notEmpty) ||
			(Array.isArray(selectedFilterValue) && selectedFilterValue.length === 0)
		)
			return;

		const filterObject = {
			column: selectedFilterColumn,
			action: selectedFilterAction,
			value: selectedFilterValue
		};
		addFilterObject(filterObject);
		closePopper && closePopper();
		clearFilter();
	};

	const handleCloseFilter = () => {
		handleApplyFilterButtonClick();
		clearFilter();
	};

	const handleRemoveAllFiltersButtonClick = () => {
		const showDefaultFilterError = filterModel.find(
			(filter) => filter.column.filterRemovable === false || filter.column.filterVisible === false
		);
		if (showDefaultFilterError) {
			showWarning({
				header: 'errorMsg.defaultFilterError',
				message: 'defaultFilterRemoved'
			});
		}

		const newFilters = filterModel.filter(
			(filter) => filter.column.filterRemovable === false || filter.column.filterVisible === false
		);
		onFilterModelChange(newFilters);
	};

	const handleFilterMenuItemClick = (option, inputType) => {
		if (selectedFilterAction === option) return;
		setSelectedFilterAction(option);

		if (option === FilterAction.multipleFilter) {
			setSelectedFilterValue([]);
		} else if (option === FilterAction.between) {
			setSelectedFilterValue({
				start: dayjs().subtract(6, 'day').startOf('day').toISOString(),
				end: dayjs().endOf('day').toISOString()
			});
		} else if (inputType !== selectedFilterInputType.current) {
			setSelectedFilterValue('');
		}
	};

	const handleFilterColumnClick = (column, updatePosition) => {
		setSelectedFilterColumn(column);

		const oldFilter = filterModel.find((obj) => obj.column.accessor === column.accessor);

		if (oldFilter) {
			setSelectedFilterAction(oldFilter.action);
			setSelectedFilterValue(oldFilter.value);
			return;
		}

		const defaultFilterAction = DefaultFilterActionMap[column.filterType];
		if (column.customFilterOptions && !column.customFilterOptions?.includes(defaultFilterAction)) {
			setSelectedFilterAction(column.customFilterOptions[0]);
		} else {
			setSelectedFilterAction(defaultFilterAction);
		}

		if (column.filterType === FilterType.date) {
			setSelectedFilterValue({ start: dayjs().startOf('day').toISOString() });
		} else {
			setSelectedFilterValue('');
		}
		updatePosition && updatePosition();
	};

	const [filterColumnsMenuSearchValue, setFilterColumnsMenuSearchValue] = useState(null);
	const [filteredFilterColumnsMenuColumns, setFilteredFilterColumnsMenuColumns] = useState(
		columns.filter((col) => col.show !== false)
	);
	useUpdateEffect(() => {
		const viewableColumns = columns.filter((col) => col.show !== false);
		if (filterColumnsMenuSearchValue) {
			setFilteredFilterColumnsMenuColumns(
				viewableColumns.filter((col) =>
					t(col.Header).toLowerCase().includes(filterColumnsMenuSearchValue.toLowerCase())
				)
			);
		} else {
			setFilteredFilterColumnsMenuColumns(viewableColumns);
		}
	}, [filterColumnsMenuSearchValue, columns]);

	const handleRemoveFilterButtonClick = (index) => {
		const temp = [...filterModel];
		temp.splice(index, 1);
		onFilterModelChange(temp);
		clearFilter();
	};

	const handleDeleteFilterButtonClick = () => {
		const deleteIndex = filterModel.findIndex((obj) => obj.column.accessor === selectedFilterColumn.accessor);
		deleteIndex !== -1 && handleRemoveFilterButtonClick(deleteIndex);
	};

	const FilterColumnsMenu = (updatePosition, className) => (
		<S.Menu className={`columnsMenu ${className ?? ''}`}>
			<Typography
				variant="p1"
				fontWeight="500"
				component="h2"
				noWrap
				style={{ overflow: 'unset' }}
				className="desktop-hidden">
				filters
			</Typography>

			<SearchInput value={filterColumnsMenuSearchValue} setValue={setFilterColumnsMenuSearchValue} />
			{filteredFilterColumnsMenuColumns.map((column, index) => {
				if (column.Header)
					return (
						<S.MenuItem
							key={index}
							className={selectedFilterColumn?.Header === column.Header ? 'selected' : ''}
							onClick={() => handleFilterColumnClick(column, updatePosition)}>
							{filterModel.some((obj) => obj.column.accessor === column.accessor) && (
								<S.MobileColumnDot />
							)}
							<Typography fontWeight="500">{column.Header}</Typography>
						</S.MenuItem>
					);
			})}
			<S.FilterColumnsButtonContainer className="desktop-hidden">
				<Button
					id="dynamiclist-reset-filter-button"
					className="dynamiclist-reset-filter-button"
					label="reset"
					icon={faTrashAlt}
					onClick={() => {
						onFilterModelChange([]);
					}}
					variant="outline"
				/>
			</S.FilterColumnsButtonContainer>
		</S.Menu>
	);

	const FilterMenu = (closePopper, className) => {
		const currFilterType = selectedFilterColumn?.filterType;
		const currFilterConfig = FilterConfig[currFilterType];
		const currCustomOptions = selectedFilterColumn?.customFilterOptions;

		return (
			currFilterConfig && (
				<S.Menu className={`filterOptions ${className ?? ''}`}>
					<div key={selectedFilterColumn.accessor}>
						{currFilterConfig
							.filter((config) => (currCustomOptions ? currCustomOptions.includes(config.action) : true))
							.map((config) => {
								const { action, inputType, multiple, rangeMode } = config;
								const isSelectedAction = selectedFilterAction === action;
								if (isSelectedAction) selectedFilterInputType.current = inputType ?? null;

								return (
									<React.Fragment key={config.action}>
										<S.FilterMenuItem
											onClick={() => handleFilterMenuItemClick(action, inputType)}
											className={isSelectedAction ? 'selected' : ''}>
											<Radio readOnly checked={isSelectedAction} />
											<Typography fontWeight="500">{`filter.${action}`}</Typography>
										</S.FilterMenuItem>
										{isSelectedAction &&
											{
												[FilterInput.string]: (
													<DynamicListStringInput
														value={selectedFilterValue}
														setValue={(value) => setSelectedFilterValue(value ?? '')}
														onEnterKeyDown={() => handleApplyFilterButtonClick(closePopper)}
													/>
												),
												[FilterInput.chips]: (
													<DynamicListChips
														value={selectedFilterValue}
														onChange={setSelectedFilterValue}
													/>
												),
												[FilterInput.number]: (
													<DynamicListNumberInput
														value={selectedFilterValue}
														setValue={(value) => setSelectedFilterValue(value ?? '')}
														onEnterKeyDown={() => handleApplyFilterButtonClick(closePopper)}
													/>
												),
												[FilterInput.float]: (
													<DynamicListFloatInput
														value={selectedFilterValue}
														setValue={setSelectedFilterValue}
														onEnterKeyDown={() => handleApplyFilterButtonClick(closePopper)}
													/>
												),
												[FilterInput.selection]: (
													<DynamicListMultiSelect
														accessor={selectedFilterColumn.accessor}
														fetchUrl={fetchUrl}
														value={selectedFilterValue}
														setValue={setSelectedFilterValue}
														labelPrefix={selectedFilterColumn.filterPrefix}
														filters={multiSelectFilters}
														requestParameters={requestParameters}
														onApply={() => handleApplyFilterButtonClick(closePopper)}
														multipleSelection={multiple}
														showMultipleSelectionButtons={multiple}
													/>
												),
												[FilterInput.enum]: (
													<DynamicListEnumSelect
														placeholder={`Select ${t(selectedFilterColumn.Header)}`}
														enumName={selectedFilterColumn.enumName}
														value={selectedFilterValue}
														setValue={setSelectedFilterValue}
														labelPrefix={selectedFilterColumn.filterPrefix}
														data={selectedFilterColumn.enumData}
														onApply={() => handleApplyFilterButtonClick(closePopper)}
														multipleSelection={multiple}
														showMultipleSelectionButtons={multiple}
													/>
												),
												[FilterInput.date]: (
													<DynamicListDateInput
														value={selectedFilterValue}
														setValue={setSelectedFilterValue}
														rangeMode={rangeMode}
													/>
												)
											}[inputType]}
									</React.Fragment>
								);
							})}
					</div>
					<S.ButtonContainer>
						<Button
							id="dynamiclist-filter-delete-button"
							onClick={handleDeleteFilterButtonClick}
							variant="transparent"
							color="red"
							label="filter.delete"
							className="dynamiclist-filter-delete-button desktop-hidden"
							disabled={
								filterModel.findIndex(
									(obj) => obj.column.accessor === selectedFilterColumn.accessor
								) === -1
							}
						/>
						<Button
							id="dynamiclist-filter-cancel-button"
							className="dynamiclist-filter-cancel-button"
							label="cancel"
							onClick={() => {
								handleCancelFilterButtonClick();
								closePopper && closePopper();
							}}
							variant="outline"
						/>
						<Button
							id="dynamiclist-apply-filter-button"
							className="dynamiclist-apply-filter-button"
							label="filter.applyFilter"
							onClick={() => handleApplyFilterButtonClick(closePopper)}
						/>
					</S.ButtonContainer>
				</S.Menu>
			)
		);
	};

	const [columnsMenuSearchValue, setColumnsMenuSearchValue] = useState(null);
	const [filteredColumnsMenuColumns, setFilteredColumnsMenuColumns] = useState(columns);
	useEffect(() => {
		if (columnsMenuSearchValue) {
			setFilteredColumnsMenuColumns(
				columns.filter((col) => t(col.Header).toLowerCase().includes(columnsMenuSearchValue.toLowerCase()))
			);
		} else {
			setFilteredColumnsMenuColumns(columns);
		}
	}, [columnsMenuSearchValue, columns]);

	const [trashButtonRef, setTrashButtonRef] = useState(null);
	const [columnsMenuButtonRef, setColumnsMenuButtonRef] = useState(null);
	const [filtersMenuButtonRef, setFiltersMenuButtonRef] = useState(null);

	const dispatch = useDispatch();
	const listMapView = useSelector((state) => state.layout.listMapView);

	const handleSelectAllClick = () => {
		const isSelectAll = !columns.some((column) => column.show === false);
		const columnsTemplate = structuredClone(originalColumns);

		if (!isSelectAll) {
			const convertedColumns = columnsTemplate.map((column) => {
				column.show = true;
				return column;
			});
			setColumns(convertedColumns);
			setHiddenColumns([]);
			setTableHiddenColumns([]);
		} else {
			const convertedColumns = columnsTemplate.map((column) => {
				column.show = false;
				return column;
			});
			setColumns(convertedColumns);
			const hiddenColumnsTemplate = columnsTemplate.map((column) => column.accessor);
			setHiddenColumns(hiddenColumnsTemplate);
			setTableHiddenColumns(hiddenColumnsTemplate);
		}
	};

	const isFilteredColumns = useMemo(() => !!columnsMenuSearchValue, [columnsMenuSearchValue]);

	const renderDndItem = useCallback(
		(column, index) => (
			<ColumnsDndItem
				key={column.accessor}
				index={index}
				column={column}
				onColumnOrderChange={setColumns}
				onColumnVisibilityChange={handleColumnVisibilityChange}
				isSingleVisibleColumn={isSingleVisibleColumn}
				isFilteredColumns={isFilteredColumns}
			/>
		),
		[columns, isFilteredColumns]
	);

	return (
		<>
			{showHeader && (
				<>
					<S.MenuContainer>
						<S.LeftMenuWrapper>
							{isMobile && showMapViewButton && (
								<Button
									id="dynamiclist-map-view-button"
									className="dynamiclist-map-view-button"
									icon={listMapView ? faList : faLocationDot}
									onClick={() => dispatch(setListMapView(!listMapView))}
									variant="action"
									iconSize="medium"
								/>
							)}

							{showFilters && (
								<>
									<Tooltip title="filter" placement="top">
										<Button
											id="dynamiclist-filter-button"
											className="dynamiclist-filter-button"
											ref={setFiltersMenuButtonRef}
											icon={faFilter}
											iconSize="medium"
											variant={isMobile ? 'action' : 'borderless'}>
											{isFiltered && <S.MobileFilterDot />}
										</Button>
									</Tooltip>

									{filtersMenuButtonRef && (
										<Popper
											refElement={filtersMenuButtonRef}
											placement="bottom-end"
											onClose={handleCloseFilter}>
											{(closePopper, updatePosition) => (
												<S.MenuWrapper>
													{selectedFilterColumn && FilterMenu(closePopper)}
													{((isMobile && !selectedFilterColumn) || !isMobile) &&
														FilterColumnsMenu(updatePosition)}
												</S.MenuWrapper>
											)}
										</Popper>
									)}
								</>
							)}

							<Tooltip title="columns" placement="top">
								<Button
									id="dynamiclist-columns-button"
									className="dynamiclist-columns-button"
									ref={setColumnsMenuButtonRef}
									icon={faList}
									iconSize="medium"
									variant={isMobile ? 'action' : 'borderless'}
								/>
							</Tooltip>
							{columnsMenuButtonRef && (
								<Popper refElement={columnsMenuButtonRef} placement="bottom-end">
									{() => (
										<S.ColumnsMenu>
											<SearchInput
												value={columnsMenuSearchValue}
												setValue={setColumnsMenuSearchValue}
												fullWidth
											/>
											{window.developerMode && columnsMenuSearchValue === null && (
												<S.ColumnsMenuItem
													onClick={handleSelectAllClick}
													className={`selectAll ${!columns.some((column) => column.show === false) ? 'selected' : ''}`}>
													<Checkbox
														checked={!columns.some((column) => column.show === false)}
														readOnly
													/>
													<Typography>dynamiclist.columns.selectAll</Typography>
												</S.ColumnsMenuItem>
											)}
											<DndProvider backend={HTML5Backend}>
												{filteredColumnsMenuColumns.map((column, index) => {
													if (column.Header) return renderDndItem(column, index);
												})}
											</DndProvider>
											<Button
												id="dynamiclist-reset-columns-button"
												className="dynamiclist-reset-columns-button"
												label="resetColumns"
												variant="transparent"
												color="red"
												onClick={resetColumns}
												fullWidth
											/>
										</S.ColumnsMenu>
									)}
								</Popper>
							)}
							{exportName && (
								<DynamicListExportButton
									columns={columns}
									filterModel={filterModel}
									sortModel={sortModel}
									exportName={exportName}
									exportNameTranslationObject={exportNameTranslationObject}
									fetchUrl={fetchUrl}
								/>
							)}
						</S.LeftMenuWrapper>
						{!isMobile && <div style={{ flexGrow: '1' }}></div>}

						{!isMobile && (
							<S.RightMenuWrapper>
								<S.MenuWrapper>
									{actionsMenu &&
										actionsMenu?.map((menu, i) => (
											<React.Fragment key={`actionsmenu_${i}`}>
												<ActionsMenu
													id={menu.label}
													selectionModel={selectionModel}
													buttonIcon={menu.icon}
													buttonLabel={menu.label}
													menuItems={menu.items}
													variant={menu.variant}
													disabled={selectionModel.length === 0}
												/>
												{i !== actionsMenu.length - 1 && <S.Line />}
											</React.Fragment>
										))}

									{selectionModel.length > 0 && (
										<>
											<S.Line />
											<S.RemoveFilterContainer>
												<Typography color="var(--content-2)">
													<Trans
														i18nKey="table.selectedRows"
														values={{
															count: selectionModel.length
														}}
													/>
												</Typography>

												<Button
													id="dynamiclist-clear-selection-button"
													className="dynamiclist-clear-selection-button"
													variant="blueBorderless"
													label="clearSelection"
													size="small"
													css={S.RemoveFilterButtonCss}
													onClick={() => {
														onSelectionModelChange([]);
														toggleAllRowsSelected(false);
													}}
												/>
											</S.RemoveFilterContainer>
										</>
									)}
								</S.MenuWrapper>
							</S.RightMenuWrapper>
						)}
						{isMobile && selectionModel.length > 0 && (
							<S.MobileRemoveFilterContainer>
								<Typography color="var(--white)">
									<Trans
										i18nKey="table.selectedRows"
										values={{
											count: selectionModel.length
										}}
									/>
								</Typography>

								<Button
									id="dynamiclist-clear-selection-button"
									className="dynamiclist-clear-selection-button"
									variant="blueBorderless"
									label="clearSelection"
									size="small"
									css={S.RemoveFilterButtonCss}
									onClick={() => {
										onSelectionModelChange([]);
										toggleAllRowsSelected(false);
									}}
								/>
							</S.MobileRemoveFilterContainer>
						)}
					</S.MenuContainer>
					{isMobile && actionsMenu && (
						<S.MobileMenuContainer>
							{actionsMenu?.map((menu, i) => (
								<ActionsMenu
									key={i}
									selectionModel={selectionModel}
									id={menu.label}
									buttonIcon={menu.icon}
									buttonLabel={menu.variant ? menu.label : undefined}
									menuItems={menu.items}
									variant={menu.variant}
									disabled={selectionModel.length === 0}
									showArrow={false}
								/>
							))}
						</S.MobileMenuContainer>
					)}
				</>
			)}
			{showFilters && isFiltered && (
				<S.FilterContainer>
					{filterModel.map((filter, index) => {
						if (filter.column.filterVisible !== false) {
							return (
								<FilterListItem
									key={index}
									itemIndex={index}
									filter={filter}
									FilterMenu={FilterMenu}
									handleRemoveFilterButtonClick={handleRemoveFilterButtonClick}
									handleCloseFilter={handleCloseFilter}
									setSelectedFilterColumn={setSelectedFilterColumn}
									setSelectedFilterAction={setSelectedFilterAction}
									setSelectedFilterValue={setSelectedFilterValue}
								/>
							);
						}
					})}

					<S.RemoveFilterContainer>
						<Typography color="var(--content-2)">
							<Trans
								i18nKey="filtersApplied"
								values={{
									count: filterModel.filter((filter) => filter.column.filterVisible !== false).length
								}}
							/>
						</Typography>

						<Button
							id="dynamiclist-clear-filters-button"
							className="dynamiclist-clear-filters-button"
							ref={setTrashButtonRef}
							variant="blueBorderless"
							label="clearFilters"
							size="small"
							css={S.RemoveFilterButtonCss}
						/>
					</S.RemoveFilterContainer>

					<ConfirmationDialog
						buttonRef={trashButtonRef}
						title="filter.deleteConfirmationTitle"
						subTitle="filter.deleteConfirmationSubtitle"
						icon={faTrash}
						rightLabel="yes"
						rightIcon={faCheck}
						rightAction={handleRemoveAllFiltersButtonClick}
						leftLabel="cancel"
						leftIcon={faTimes}
					/>
				</S.FilterContainer>
			)}
		</>
	);
};

const FilterListItem = ({
	itemIndex,
	filter,
	FilterMenu,
	handleRemoveFilterButtonClick,
	handleCloseFilter,
	setSelectedFilterColumn,
	setSelectedFilterAction,
	setSelectedFilterValue
}) => {
	const { t } = useTranslation();
	const [buttonRef, setButtonRef] = useState(null);

	const { getLocaleDate } = useLocale();

	const getTranslatedStr = useCallback(
		({ key, filterType }) =>
			[FilterType.enum, FilterType.enumMulti, FilterType.selection, FilterType.multiSelection].includes(
				filterType
			)
				? t(key)
				: key,

		[]
	);

	const convertFilterValueToString = (filterObj) => {
		const { column, action, value } = filterObj;

		if (action === FilterAction.isEmpty || action === FilterAction.notEmpty) {
			return '';
		}

		if (action === FilterAction.multipleFilter) {
			return `${value.length} ${value.length === 1 ? t('filter') : t('filters')}`;
		}
		if (column.filterType === FilterType.float) {
			return `${value.min ?? t('min')} - ${value.max ?? t('max')}`;
		}
		if (value.start && value.end) {
			return `${getLocaleDate(value.start)} - ${getLocaleDate(value.end)}`;
		}
		if (value.start) {
			return getLocaleDate(value.start) === getLocaleDate() ? t('today') : getLocaleDate(value.start);
		}

		return Array.isArray(value)
			? value
					.map((val) =>
						getTranslatedStr({
							key: val,
							filterType: column.filterType
						})
					)
					.join(', ')
			: getTranslatedStr({
					key: value,
					filterType: column.filterType
				});
	};

	if (filter.column.filterVisible === false) return;

	return (
		<>
			<Tag
				ref={setButtonRef}
				onClick={() => {
					setSelectedFilterColumn(filter.column);
					setSelectedFilterAction(filter.action);
					setSelectedFilterValue(filter.value);
				}}
				onClose={
					filter.column.filterRemovable !== false &&
					((e) => {
						e.stopPropagation();
						handleRemoveFilterButtonClick(itemIndex);
					})
				}>
				<Typography fontWeight="700">{t(filter.column.Header)}</Typography>
				<Typography fontWeight="700">
					{`filtered.${filter.action === 'between' && !filter.value.end ? 'after' : filter.action}`}
				</Typography>
				<S.FilterValue>
					<Typography autoTranslation={false}>{convertFilterValueToString(filter)}</Typography>
				</S.FilterValue>
			</Tag>
			{buttonRef && (
				<Popper refElement={buttonRef} onClose={handleCloseFilter}>
					{(closePopper) => FilterMenu(closePopper, 'leftSide')}
				</Popper>
			)}
		</>
	);
};

export default memo(DynamicListFilter);
